import {
    Box,
    Button,
    CircularProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import {
    CheckCircleOutline,
    CloudUpload,
    ErrorOutline,
    GetApp,
    InfoOutlined,
} from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useState } from "react"
import { importarClientesPainel } from "../../../services/clientesFornecedor"
import useLoadingStore from "../../../stores/LoadingStore"
import { ImportacaoClientesActionsTypes } from "../context/importacaoClientesActions"
import { useImportacaoClientesContext } from "../context/useImportacaoClientesContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "800px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    titleRow: {
        display: "flex",
        alignItems: "center",
        marginBottom: theme.spacing(1),
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
        fontSize: "2rem",
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    subtitleText: {
        color: "#7f8c8d",
        marginBottom: theme.spacing(3),
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeaderCard: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    submitButton: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        fontWeight: 600,
        padding: theme.spacing(1.2, 4),
        boxShadow: "0 4px 12px rgba(76,175,80,0.3)",
        marginTop: theme.spacing(2),
        "&:hover": {
            backgroundColor: "#43a047",
            boxShadow: "0 6px 16px rgba(76,175,80,0.4)",
        },
        "&:disabled": {
            backgroundColor: "#a5d6a7",
            color: "white",
        },
    },
    resultCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
    },
    resultHeader: {
        backgroundColor: "#e8f5e9",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #c8e6c9",
        display: "flex",
        alignItems: "center",
    },
    erroItem: {
        borderRadius: 8,
        marginBottom: theme.spacing(0.5),
        backgroundColor: "#fff3f3",
        padding: theme.spacing(0, 1),
    },
    erroText: {
        color: "#c62828",
        fontSize: "0.875rem",
    },
    downloadLink: {
        color: "#4caf50",
        fontWeight: 600,
        textDecoration: "none",
        "&:hover": { textDecoration: "underline" },
    },
}))

export function FormularioImportacao() {
    const classes = useStyles()
    const { dados, dispatch } = useImportacaoClientesContext()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)
    const [enviando, setEnviando] = useState(false)

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    const handleImportar = async () => {
        if (!dados.conteudo.trim()) {
            alertStart("Cole o conteúdo da planilha antes de importar.", "error")
            return
        }

        const authorization = localStorage.getItem("chaveSistema")
        if (!authorization) {
            alertStart("Sessão expirada. Faça login novamente.", "error")
            return
        }

        setEnviando(true)
        setLoading(true)
        try {
            const response = await importarClientesPainel(authorization, dados.conteudo)
            if ("retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao importar clientes.", "error")
            } else {
                dispatch({
                    type: ImportacaoClientesActionsTypes.DEFINIR_RESULTADO,
                    payload: response,
                })
                if (response.importados === 1) {
                    alertStart("Importação finalizada, 1 registro importado.", "success")
                } else if (response.importados > 1) {
                    alertStart(
                        `Importação finalizada, ${response.importados} registros importados.`,
                        "success"
                    )
                } else {
                    alertStart("Nenhum registro importado.", "info")
                }
            }
        } finally {
            setEnviando(false)
            setLoading(false)
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Box className={classes.titleRow}>
                    <CloudUpload className={classes.iconHeader} />
                    <Typography variant="h5" className={classes.titleText}>
                        Importação de Clientes
                    </Typography>
                </Box>
                <Typography variant="body2" className={classes.subtitleText}>
                    Cole o conteúdo de uma planilha para importar clientes em lote.
                </Typography>

                <Paper className={classes.configCard} elevation={0}>
                    <Box className={classes.cardHeader}>
                        <CloudUpload className={classes.iconHeaderCard} />
                        <Typography variant="subtitle1" className={classes.cardHeaderTitle}>
                            Dados para importação
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <TextField
                            multiline
                            rows={12}
                            variant="outlined"
                            fullWidth
                            placeholder="Cole aqui o conteúdo copiado da planilha..."
                            value={dados.conteudo}
                            onChange={e =>
                                dispatch({
                                    type: ImportacaoClientesActionsTypes.ATUALIZAR_CONTEUDO,
                                    payload: e.target.value,
                                })
                            }
                        />
                        <Button
                            className={classes.submitButton}
                            onClick={handleImportar}
                            disabled={enviando}
                            startIcon={
                                enviando ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <CloudUpload />
                                )
                            }
                        >
                            {enviando ? "Importando..." : "Importar"}
                        </Button>
                    </Box>
                </Paper>

                {dados.resultado && (
                    <Paper className={classes.resultCard} elevation={0}>
                        <Box className={classes.resultHeader}>
                            <CheckCircleOutline style={{ color: "#2e7d32", marginRight: 8 }} />
                            <Typography
                                variant="subtitle1"
                                style={{ fontWeight: 600, color: "#2e7d32" }}
                            >
                                Resultado da importação
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            <Typography variant="body1" style={{ marginBottom: 8 }}>
                                <strong>{dados.resultado.importados}</strong> registro(s)
                                importado(s) com sucesso.
                            </Typography>
                            {dados.resultado.erros.length > 0 && (
                                <>
                                    <Typography
                                        variant="body2"
                                        style={{
                                            color: "#c62828",
                                            fontWeight: 600,
                                            marginBottom: 4,
                                        }}
                                    >
                                        {dados.resultado.erros.length} erro(s) encontrado(s):
                                    </Typography>
                                    <List dense disablePadding>
                                        {dados.resultado.erros.map((erro, i) => (
                                            <ListItem key={i} className={classes.erroItem}>
                                                <ListItemIcon style={{ minWidth: 28 }}>
                                                    <ErrorOutline
                                                        style={{ color: "#c62828", fontSize: 18 }}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={erro}
                                                    primaryTypographyProps={{
                                                        className: classes.erroText,
                                                    }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </>
                            )}
                        </Box>
                    </Paper>
                )}

                <Paper className={classes.configCard} elevation={0}>
                    <Box className={classes.cardHeader}>
                        <InfoOutlined className={classes.iconHeaderCard} />
                        <Typography variant="subtitle1" className={classes.cardHeaderTitle}>
                            Como preencher a planilha
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Typography variant="body2" style={{ marginBottom: 8 }}>
                            Abra o Microsoft Excel (ou similar) e preencha as colunas na seguinte
                            ordem:
                        </Typography>
                        <List dense disablePadding style={{ marginBottom: 16 }}>
                            {[
                                "Coluna 1 (obrigatória): Nome do cliente",
                                "Coluna 2 (obrigatória): Telefone",
                                "Coluna 3 (opcional): Logradouro",
                                "Coluna 4 (opcional): Número",
                                "Coluna 5 (opcional): Complemento",
                                "Coluna 6 (opcional): Bairro",
                                "Coluna 7 (opcional): Cidade",
                                "Coluna 8 (opcional): UF (sigla)",
                                "Coluna 9 (opcional): Observação",
                            ].map((item, i) => (
                                <ListItem
                                    key={i}
                                    disableGutters
                                    style={{ paddingTop: 2, paddingBottom: 2 }}
                                >
                                    <ListItemText
                                        primary={item}
                                        primaryTypographyProps={{ variant: "body2" }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                        <Typography variant="body2" style={{ marginBottom: 8 }}>
                            Selecione as células com os dados, copie (<strong>Ctrl+C</strong>) e
                            cole no campo acima. Em seguida, clique em <strong>Importar</strong>.
                        </Typography>
                        <Typography variant="body2" style={{ marginBottom: 12 }}>
                            Após a importação, verifique os cadastros acessando o painel de clientes
                            e pesquisando pelos nomes recém importados.
                        </Typography>
                        <a
                            href="https://clientefiel.s3.us-east-1.amazonaws.com/ExemploImportacaoCliente.xlsx"
                            className={classes.downloadLink}
                        >
                            <GetApp
                                style={{ fontSize: 16, verticalAlign: "middle", marginRight: 4 }}
                            />
                            Baixar planilha de exemplo
                        </a>
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
